import { useCallback, useEffect, useRef, useState } from "react";
import useSocket from "./useSocket";
import {
  BUFFER_SIZE,
  SILENCE_THRESHOLD,
  TARGET_SAMPLE_RATE,
  SILENCE_TIMEOUT_MS,
  MIC_ERROR_MESSAGE_MAP,
  TRANSCRIBE_LANGUAGE_CODE,
  TRANSCRIBE_MEDIA_ENCODING,
  DUPLICATE_TRANSCRIPT_WINDOW_MS,
} from "static/transcription";
import {
  calculateRMS,
  downsampleAudioBuffer,
  convertFloat32ToPCM16,
  extractTranscriptText,
} from "utils/transcription";
import { SOCKET_SERVER_NAMESPACES } from "static/constants";

const useTranscription = ({ onError, onStart, onStop, onTranscript }) => {
  const socket = useSocket(SOCKET_SERVER_NAMESPACES.TRANSCRIBE, {
    autoConnectOnMount: false,
  });

  const [isTranscribing, setIsTranscribing] = useState(false);

  const refs = {
    stream: useRef(null),
    ctx: useRef(null),
    source: useRef(null),
    processor: useRef(null),
    gain: useRef(null),
    silenceTimer: useRef(null),
    readyTimer: useRef(null),
    lastTranscript: useRef(""),
    lastTranscriptAt: useRef(0),
    speechStarted: useRef(false),
    cleaning: useRef(false),
    serverReady: useRef(false),
  };

  const callbacks = useRef({ onError, onStart, onStop, onTranscript });

  useEffect(() => {
    callbacks.current = { onError, onStart, onStop, onTranscript };
  }, [onError, onStart, onStop, onTranscript]);

  const cleanupAudio = useCallback(async () => {
    const { processor, source, gain, ctx, stream } = refs;

    processor.current?.disconnect();
    if (processor.current) processor.current.onaudioprocess = null;

    source.current?.disconnect();
    gain.current?.disconnect();

    stream.current?.getTracks().forEach((t) => t.stop());

    if (ctx.current && ctx.current.state !== "closed") {
      await ctx.current.close();
    }

    processor.current = null;
    source.current = null;
    gain.current = null;
    ctx.current = null;
    stream.current = null;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopTranscription = useCallback(async () => {
    if (refs.cleaning.current) return;

    refs.cleaning.current = true;

    clearTimeout(refs.silenceTimer.current);
    clearTimeout(refs.readyTimer.current);
    refs.silenceTimer.current = null;
    refs.readyTimer.current = null;
    refs.lastTranscript.current = "";
    refs.lastTranscriptAt.current = 0;
    refs.speechStarted.current = false;
    refs.serverReady.current = false;

    if (socket?.connected) {
      socket.emit("transcribe:stop");
      socket.disconnect();
    }

    socket?.removeAllListeners();

    await cleanupAudio();

    setIsTranscribing(false);
    callbacks.current.onStop?.();

    refs.cleaning.current = false;
  }, [socket, cleanupAudio]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSilence = useCallback(
    (buffer) => {
      const rms = calculateRMS(buffer);

      if (rms > SILENCE_THRESHOLD) {
        refs.speechStarted.current = true;
        clearTimeout(refs.silenceTimer.current);
        refs.silenceTimer.current = null;
        return;
      }

      if (!refs.speechStarted.current || refs.silenceTimer.current) return;

      refs.silenceTimer.current = setTimeout(
        () => stopTranscription(),
        SILENCE_TIMEOUT_MS
      );
    },
    [stopTranscription] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setupAudio = useCallback(
    async (stream) => {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();

      await ctx.resume();

      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(BUFFER_SIZE, 1, 1);
      const gain = ctx.createGain();

      gain.gain.value = 0;

      refs.ctx.current = ctx;
      refs.source.current = source;
      refs.processor.current = processor;
      refs.gain.current = gain;

      processor.onaudioprocess = (e) => {
        if (!socket?.connected || !refs.serverReady.current) return;

        const input = e.inputBuffer.getChannelData(0);
        const downsampled = downsampleAudioBuffer(
          input,
          ctx.sampleRate,
          TARGET_SAMPLE_RATE
        );

        if (!downsampled.length) return;

        handleSilence(downsampled);

        const pcm = convertFloat32ToPCM16(downsampled);
        socket.emit("transcribe:audio", pcm.buffer);
      };

      source.connect(processor);
      processor.connect(gain);
      gain.connect(ctx.destination);
    },
    [socket, handleSilence]
  );

  const bindSocketEvents = useCallback(() => {
    // Reset ready state on re-binding
    clearTimeout(refs.readyTimer.current);
    refs.serverReady.current = false;
    refs.readyTimer.current = null;
    refs.lastTranscript.current = "";
    refs.lastTranscriptAt.current = 0;

    socket?.removeAllListeners();

    socket.on("connect", async () => {
      await setupAudio(refs.stream.current);

      // Send start signal and wait for server to be ready
      socket.emit("transcribe:start", {
        sampleRateHertz: TARGET_SAMPLE_RATE,
        languageCode: TRANSCRIBE_LANGUAGE_CODE,
        mediaEncoding: TRANSCRIBE_MEDIA_ENCODING,
      });
    });

    const markTranscriptionReady = () => {
      if (refs.serverReady.current) {
        return;
      }

      clearTimeout(refs.readyTimer.current);
      refs.readyTimer.current = null;
      refs.serverReady.current = true;
      setIsTranscribing(true);
      callbacks.current.onStart?.();
    };

    socket.on("transcribe:started", markTranscriptionReady);

    socket.on("transcribe:ready", markTranscriptionReady);

    // Fallback: if server doesn't send ready event within 1s, assume it's ready
    socket.once("connect", () => {
      refs.readyTimer.current = setTimeout(() => {
        if (!refs.serverReady.current) {
          markTranscriptionReady();
        }
      }, 1000);
    });

    const handleTranscript = (payload) => {
      if (!payload || payload?.isPartial) {
        return;
      }

      const transcript = extractTranscriptText(payload);

      if (!transcript) return;

      const normalizedTranscript = transcript.trim();
      const previousTranscript = refs.lastTranscript.current.trim();
      const now = Date.now();

      if (!normalizedTranscript) return;

      if (
        normalizedTranscript === previousTranscript &&
        now - refs.lastTranscriptAt.current < DUPLICATE_TRANSCRIPT_WINDOW_MS
      ) {
        return;
      }

      if (
        previousTranscript &&
        normalizedTranscript.startsWith(previousTranscript)
      ) {
        const transcriptDelta = normalizedTranscript
          .slice(previousTranscript.length)
          .trim();

        refs.lastTranscript.current = normalizedTranscript;

        if (!transcriptDelta) {
          return;
        }

        refs.lastTranscriptAt.current = now;
        callbacks.current.onTranscript?.(transcriptDelta);
        return;
      }

      refs.lastTranscript.current = normalizedTranscript;
      refs.lastTranscriptAt.current = now;

      callbacks.current.onTranscript?.(normalizedTranscript);
    };

    socket.on("transcribe:transcript", handleTranscript);

    socket.on("transcribe:error", async (e) => {
      callbacks.current.onError?.(e?.message || "Transcription error");
      await stopTranscription();
    });

    socket.on("connect_error", async () => {
      callbacks.current.onError?.("Connection failed");
      await stopTranscription();
    });

    socket.on("disconnect", async (reason) => {
      if (refs.cleaning.current || reason === "io client disconnect") return;
      callbacks.current.onError?.("Connection lost");
      await stopTranscription();
    });
  }, [socket, setupAudio, stopTranscription]); // eslint-disable-line react-hooks/exhaustive-deps

  const startTranscription = useCallback(async () => {
    if (isTranscribing) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      callbacks.current.onError?.(
        "Microphone is not supported in this browser."
      );
      return;
    }

    if (!(window.AudioContext || window.webkitAudioContext)) {
      callbacks.current.onError?.(
        "Audio processing is not supported in this browser."
      );
      return;
    }

    if (!socket) {
      callbacks.current.onError?.(
        "Transcription service is unavailable. Please sign in again and retry."
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      refs.stream.current = stream;

      // Bind socket events BEFORE connecting to avoid race conditions
      bindSocketEvents();
      socket.connect();
    } catch (err) {
      const isMediaError = Boolean(
        err?.name && MIC_ERROR_MESSAGE_MAP[err.name]
      );
      const msg = isMediaError
        ? MIC_ERROR_MESSAGE_MAP[err.name]
        : err?.message ||
          "Unable to start voice transcription. Please try again.";
      callbacks.current.onError?.(msg);
      await stopTranscription();
    }
  }, [isTranscribing, socket, stopTranscription, bindSocketEvents]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => stopTranscription();
  }, [stopTranscription]);

  return {
    isTranscribing,
    startTranscription,
    stopTranscription,
  };
};

export default useTranscription;
